<template>
     <div class="flex flex-col gap-4 h-full overflow-y-auto pt-20 md:pt-20 xl:pt-8  p-4 md:p-8">
        <div class="flex flex-col w-full gap-4">
            <div class="flex flex-col gap-1">
                <h1 class="text-xl font-modulus-semibold font-[500]">{{ $t("dashboard.auto.resumen_este_mes") }}</h1>
                <p class="text-sm font-modulus font-[300]">{{ $t("dashboard.auto.muestra_a_manera_de_resumen_todo_lo_que_se_ha_re") }}</p>
            </div>
            <div
                v-if="metricsLoadError"
                class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                role="alert"
            >{{ $t("dashboard.auto.no_se_pudieron_cargar_las_metricas_del_dashboard") }}</div>

            <div :class="['grid gap-4 sm:gap-5 lg:gap-6 grid-cols-1 sm:grid-cols-2', hasFinancialKpis ? 'md:grid-cols-3 lg:grid-cols-5' : '']">
                <div data-test="kpi-total-itineraries" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.total_itinerarios") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                                    <IconCalendar class="h-5 w-5 text-blue-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.totalItineraries ?? 0 }}</div>
                    </div>
                </div>

                <div data-test="kpi-upcoming-itineraries" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.itinerarios_proximos") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-rajah-100">
                                    <IconCalendar class="h-5 w-5 text-rajah-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.upcomingItineraries ?? 0 }}</div>
                    </div>
                </div>

                <div v-if="monthSummary.invoiced !== null" data-test="kpi-realized-income" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.ingreso_realizado") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-eblack-100">
                                    <IconCashBanknote class="h-5 w-5 text-eblack-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.invoiced }}</div>
                    </div>
                </div>

                <div v-if="monthSummary.paidCommissions !== null" data-test="kpi-realized-commissions" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.comisiones_realizadas") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-egreen-100">
                                    <IconCashBanknote class="h-5 w-5 text-egreen-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.paidCommissions }}</div>
                    </div>
                </div>

                <div v-if="monthSummary.pendingCommissions !== null" data-test="kpi-commissions-receivable" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.comisiones_por_cobrar") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-rajah-100">
                                    <IconClockHour1 class="h-5 w-5 text-rajah-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.pendingCommissions }}</div>
                    </div>
                </div>
            </div>

            <section>
                <div class="border shadow-sm p-4 rounded-2xl">
                    <div>
                        <div class="flex items-center justify-between">
                            <div>
                                <h2 class="text-lg font-semibold">{{ $t("dashboard.auto.calendario_de_reservaciones") }}</h2>
                                <p class="mt-1">{{ $t("dashboard.auto.visualiza_las_reservaciones_completadas_verde_y_") }}</p>
                            </div>
                            <div class="flex gap-4 text-sm">
                                <div class="flex items-center gap-2">
                                    <div class="h-3 w-3 rounded-full bg-green-500" />
                                    <span class="text-muted-foreground">{{ $t("dashboard.auto.completadas") }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <div class="h-3 w-3 rounded-full bg-eorange-300" />
                                    <span class="text-muted-foreground">{{ $t("dashboard.auto.proximas") }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Estructura del calendario -->
                    <div class="mt-4">
                        <!-- Calendar Navigation -->
                        <div class="flex items-center justify-between mb-4 pb-4 border-b">
                            <button 
                                @click="previousMonth" 
                                class="flex items-center justify-center h-10 w-10 rounded-lg border hover:bg-slate-50 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            
                            <div class="text-center">
                                <h3 class="text-lg font-semibold">{{ currentMonthName }} {{ currentCalendarYear }}</h3>
                                <p class="text-sm text-muted-foreground">{{ reservationsThisMonthLabel }}</p>
                            </div>
                            
                            <button 
                                @click="nextMonth" 
                                class="flex items-center justify-center h-10 w-10 rounded-lg border hover:bg-slate-50 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>

                        <!-- Calendar Grid -->
                        <div class="grid grid-cols-7 gap-2">
                            <!-- Calendar Header -->
                            <div 
                                v-for="day in weekdayLabels"
                                :key="day" 
                                class="text-center text-sm font-medium text-muted-foreground py-2"
                            >
                                {{ day }}
                            </div>

                            <!-- Calendar Days -->
                            <div
                                v-for="(day, index) in calendarDays"
                                :key="index"
                                @click="selectDay(day)"
                                :class="[
                                    'aspect-square p-2 rounded-lg border transition-all',
                                    day.isCurrentMonth 
                                        ? 'border-border hover:border-primary cursor-pointer' 
                                        : 'border-transparent bg-slate-50',
                                    day.isToday ? 'ring-2 ring-blue-500' : '',
                                    getReservationTypeClass(day)
                                ]"
                            >
                                <div v-if="day.isCurrentMonth" class="flex flex-col items-center justify-center h-full">
                                    <span 
                                        :class="[
                                            'text-sm font-medium',
                                            day.hasReservations ? 'text-foreground' : 'text-muted-foreground'
                                        ]"
                                    >
                                        {{ day.dayNumber }}
                                    </span>
                                    <div v-if="day.hasReservations" class="mt-1 flex items-center justify-center">
                                        <span 
                                            :class="[
                                                'text-xs font-semibold px-1.5 py-0.5 rounded-full',
                                                day.type === 'completed' 
                                                    ? 'bg-green-500 text-white' 
                                                    : 'bg-eorange-300 text-white'
                                            ]"
                                        >
                                            {{ day.count }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Selected Day Reservations -->
                        <div v-if="selectedDayReservations.length > 0" class="mt-6 pt-6 border-t border-border">
                            <div class="flex items-center justify-between mb-4">
                                <h3 class="text-sm font-semibold">
                                    {{ selectedDayReservationTitle }}
                                </h3>
                                <button 
                                    @click="clearSelection" 
                                    class="text-sm text-muted-foreground hover:text-foreground"
                                >{{ $t("dashboard.auto.cerrar") }}</button>
                            </div>
                            <div class="space-y-3">
                                <div
                                    v-for="(reservation, idx) in selectedDayReservations"
                                    :key="idx"
                                    :class="[
                                        'flex items-center justify-between p-3 rounded-lg border transition-colors',
                                        reservation.type === 'completed'
                                            ? 'bg-green-50 border-green-200 hover:bg-green-100'
                                            : 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                                    ]"
                                >
                                    <div class="flex items-center gap-3">
                                        <div 
                                            :class="[
                                                'flex h-10 w-10 items-center justify-center rounded-full font-semibold text-sm',
                                                reservation.type === 'completed'
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-eorange-300 text-white'
                                            ]"
                                        >
                                            {{ reservation.dayNumber }}
                                        </div>
                                        <div>
                                            <p class="text-sm font-medium text-foreground">{{ reservation.customer_name }}</p>
                                            <p class="text-xs text-muted-foreground">{{ reservation.code }}</p>
                                        </div>
                                    </div>
                                    <div class="text-right">
                                        <p class="text-sm font-medium text-foreground">{{ reservation.time }}</p>
                                        <p class="text-xs text-muted-foreground">${{ reservation.total }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div :class="['grid gap-4 sm:gap-5 lg:gap-6 grid-cols-1', generalSummary ? 'lg:grid-cols-2' : '']">
                <div v-if="generalSummary" data-test="general-summary" class="border shadow-sm p-4 rounded-2xl">
                    <div>
                        <div class="flex items-center justify-between">
                            <div>
                                <h2 class="text-lg font-semibold text-eblack-800">{{ $t("dashboard.auto.resumen_general") }}</h2>
                                <p class="mt-1">{{ $t("dashboard.auto.muestra_a_manera_de_resumen_todo_lo_que_se_ha_re_i7ya5") }}</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div class="space-y-4">
                            <div class="flex items-center justify-between py-3 border-b">
                                <span class="text-sm font-medium text-muted-foreground">{{ $t("dashboard.auto.total_itinerarios") }}</span>
                                <span class="text-2xl font-bold text-foreground">{{ generalSummary.totalItineraries }}</span>
                            </div>

                            <div class="flex items-center justify-between py-3 border-b">
                                <span class="text-sm font-medium text-muted-foreground">{{ $t("dashboard.auto.ingreso_realizado_total") }}</span>
                                <span class="text-2xl font-bold text-foreground">${{ generalSummary.invoiced }}</span>
                            </div>

                            <div class="flex items-center justify-between py-3 border-b">
                                <span class="text-sm font-medium text-muted-foreground">{{ $t("dashboard.auto.comisiones_realizadas") }}</span>
                                <span class="text-2xl font-bold text-foreground">{{ generalSummary.totalCommissions }}</span>
                            </div>

                            <div class="flex items-center justify-between py-3">
                                <span class="text-sm font-medium text-muted-foreground">{{ $t("dashboard.auto.itinerarios_cancelados") }}</span>
                                <span class="text-2xl font-bold text-foreground">{{ generalSummary.cancelledCommissions }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div data-test="itineraries-section" class="border shadow-sm p-4 rounded-2xl">
                    <div>
                        <div class="flex items-center justify-between">
                            <div>
                                <h2 class="text-lg font-semibold text-eblack-800">{{ $t("dashboard.auto.itinerarios") }}</h2>
                                <p class="mt-1">{{ $t("dashboard.auto.te_permite_visualizar_los_diferentes_itinerarios") }}</p>
                            </div>
                            <DashboardDropdown :options="stateDropdownOptions"/>
                        </div>
                    </div>

                    <div>
                        <div class="flex-1 flex flex-col gap-4 overflow-y-auto" v-if="tempItineraries.length > 0">
                            <div class="flex flex-col w-full border rounded-lg p-2" v-for="itinerary in tempItineraries" :key="itinerary.id"> 
                                <div class="flex justify-between items-center" >
                                    <div class="flex flex-col gap-1">
                                        <h1>{{ itinerary.customer_name }} ({{itinerary.code}})</h1>
                                        <div class="flex flex-col gap-2">
                                            <span class="text-md font-semibold">${{ itinerary.total }}</span>
                                            <span class="text-sm">
                                                {{ $t("dashboard.auto.start_label") }}: {{ formatDate(itinerary.start_date) }} -
                                                {{ $t("dashboard.auto.end_label") }}: {{ formatDate(itinerary.end_date) }}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <span class="text-sm">{{itinerary.state}}</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="flex flex-col items-center justify-center py-12">
                            <div class="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
                                <IconCalendar class="h-8 w-8 text-slate-400" />
                            </div>
                            <p class="text-sm text-muted-foreground">{{ $t("dashboard.auto.aun_no_hay_itinerarios") }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="commissionsAvailable" data-test="commissions-section" class="border shadow-sm p-4 rounded-2xl">
                <div>
                    <div class="flex items-center justify-between">
                        <div>
                            <div class="text-lg font-semibold text-eblack-800">{{ $t("dashboard.auto.comisiones") }}</div>
                        </div>

                        <DashboardDropdown 
                        :options="yearsDropdownOptions"
                        @load-data="loadCommissionsData($event)"/>
                    </div>
                </div>
                <div>
                    <DashboardChart  :data="chartData" />
                </div>
            </div>
        </div>
     </div>
   
</template>

<script>
import DashboardChart from './DashboardChart.vue';

import dashboardRequests from '@/services/admin/dashboard-service';
import DashboardDropdown from './DashboardDropdown.vue';
import { IconCalendar, IconCashBanknote, IconClockHour1 } from '@tabler/icons-vue';

export default {
    name:"ConciergeDashboard",
    data(){
        return {
            showDetails: true,
            itineraries: [],
            // Financial fields stay null until the API returns them; the API
            // withholds them (null) for members without org financial access.
            monthSummary: {
                totalItineraries:0,
                upcomingItineraries:0,
                invoiced:null,
                paidCommissions:null,
                pendingCommissions:null
            },
            generalSummary: null,
            commissionsAvailable: false,
            allItineraries: [],
            upcomingItineraries:[],
            currentYear: new Date().getFullYear(),
            paidCommissions:[],
            pendingCommissions:[],
            chartData:[],
            tempItineraries:[],
            metricsLoadError: false,
            yearsDropdownOptions: {
                values:[2025,2026,2027],
                default: new Date().getFullYear()
            },
            // Calendar data
            currentCalendarMonth: new Date().getMonth(),
            currentCalendarYear: new Date().getFullYear(),
            selectedDay: null,
            selectedDayReservations: [],
        }
    },
    computed: {
        hasFinancialKpis() {
            return this.monthSummary.invoiced !== null
                || this.monthSummary.paidCommissions !== null
                || this.monthSummary.pendingCommissions !== null;
        },
        currentLocale() {
            const locale = this.$i18n?.locale;
            return typeof locale === "string" ? locale : locale?.value || "es";
        },
        monthNames() {
            return [
                this.$t("dashboard.auto.months.january"),
                this.$t("dashboard.auto.months.february"),
                this.$t("dashboard.auto.months.march"),
                this.$t("dashboard.auto.months.april"),
                this.$t("dashboard.auto.months.may"),
                this.$t("dashboard.auto.months.june"),
                this.$t("dashboard.auto.months.july"),
                this.$t("dashboard.auto.months.august"),
                this.$t("dashboard.auto.months.september"),
                this.$t("dashboard.auto.months.october"),
                this.$t("dashboard.auto.months.november"),
                this.$t("dashboard.auto.months.december"),
            ];
        },
        weekdayLabels() {
            return [
                this.$t("dashboard.auto.weekdays_short.sunday"),
                this.$t("dashboard.auto.weekdays_short.monday"),
                this.$t("dashboard.auto.weekdays_short.tuesday"),
                this.$t("dashboard.auto.weekdays_short.wednesday"),
                this.$t("dashboard.auto.weekdays_short.thursday"),
                this.$t("dashboard.auto.weekdays_short.friday"),
                this.$t("dashboard.auto.weekdays_short.saturday"),
            ];
        },
        stateDropdownOptions() {
            return {
                values: [
                    this.$t("dashboard.auto.dropdown.upcoming"),
                    this.$t("dashboard.auto.dropdown.pending"),
                    this.$t("dashboard.auto.dropdown.approved"),
                    this.$t("dashboard.auto.dropdown.paid"),
                    this.$t("dashboard.auto.dropdown.cancelled"),
                ],
                default: this.$t("dashboard.auto.dropdown.upcoming"),
            };
        },
        currentMonthName() {
            return this.monthNames[this.currentCalendarMonth];
        },
        reservationsThisMonthLabel() {
            const count = this.getTotalReservationsForMonth();
            return this.$t(
                count === 1
                    ? "dashboard.auto.reservation_this_month"
                    : "dashboard.auto.reservations_this_month",
                { count },
            );
        },
        selectedDayReservationTitle() {
            if (!this.selectedDay) return "";
            return this.$t("dashboard.auto.selected_day_reservations", {
                day: this.selectedDay.dayNumber,
                month: this.currentMonthName,
            });
        },
        calendarDays() {
            const year = this.currentCalendarYear;
            const month = this.currentCalendarMonth;
            
            // Get first day of month and total days
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            const daysInMonth = lastDay.getDate();
            const startingDayOfWeek = firstDay.getDay();
            
            // Get previous month's last days
            const prevMonthLastDay = new Date(year, month, 0).getDate();
            
            const days = [];
            
            // Add previous month's days
            for (let i = startingDayOfWeek - 1; i >= 0; i--) {
                days.push({
                    dayNumber: prevMonthLastDay - i,
                    isCurrentMonth: false,
                    date: new Date(year, month - 1, prevMonthLastDay - i)
                });
            }
            
            // Add current month's days
            for (let i = 1; i <= daysInMonth; i++) {
                const date = new Date(year, month, i);
                const reservations = this.getReservationsForDate(date);
                const today = new Date();
                const isToday = date.getDate() === today.getDate() && 
                               date.getMonth() === today.getMonth() && 
                               date.getFullYear() === today.getFullYear();
                
                days.push({
                    dayNumber: i,
                    isCurrentMonth: true,
                    date: date,
                    hasReservations: reservations.length > 0,
                    count: reservations.length,
                    type: this.getReservationType(date),
                    isToday: isToday
                });
            }
            
            // Add next month's days to complete grid (42 cells = 6 weeks)
            const remainingDays = 42 - days.length;
            for (let i = 1; i <= remainingDays; i++) {
                days.push({
                    dayNumber: i,
                    isCurrentMonth: false,
                    date: new Date(year, month + 1, i)
                });
            }
            
            return days;
        }
    },
    components:{
        DashboardChart,
        DashboardDropdown,
        IconCalendar,
        IconCashBanknote,
        IconClockHour1
    },
    watch: {
        "$i18n.locale"() {
            this.prepareChartData();
        },
    },
    methods:{
        getMetrics(){
            this.metricsLoadError = false
            dashboardRequests.getConciergeMetrics(this.currentYear).then(
                res => {
                    if(res.status == 200){
                        this.metricsLoadError = false
                        let {data} = res
                        let {monthlySummary, generalSummary,upcomingItineraries = [],allItineraries = []} = data
                        
                        this.monthSummary.totalItineraries = monthlySummary.itineraries_this_month
                        this.monthSummary.upcomingItineraries = upcomingItineraries.length
                        this.monthSummary.invoiced = monthlySummary.total_billed_this_month ?? null
                        this.monthSummary.paidCommissions = monthlySummary.total_commissions_this_month ?? null
                        this.monthSummary.pendingCommissions = monthlySummary.approved_commissions_this_month ?? null

                        this.generalSummary = generalSummary
                            ? {
                                totalItineraries: generalSummary.total_itineraries,
                                invoiced: generalSummary.total_invoiced ?? 0,
                                totalCommissions: generalSummary.total_commissions ?? 0,
                                cancelledCommissions: generalSummary.total_cancelled ?? 0,
                            }
                            : null

                        const normalizedAllItineraries = (allItineraries || []).map(this.normalizeDashboardItinerary)
                        const normalizedUpcomingItineraries = (upcomingItineraries || []).map(this.normalizeDashboardItinerary)

                        this.allItineraries = normalizedAllItineraries
                        this.upcomingItineraries = normalizedUpcomingItineraries
                        this.tempItineraries = normalizedUpcomingItineraries
 
                    }
                }
            ).catch(error => {
                this.metricsLoadError = true
                console.error(error)
            })
        },
        setCardSummary(){
            console.log(this.itineraries.length)
            let date = new Date();
            this.cardsSummary.itinerariesRealized = this.itineraries.length

            this.cardsSummary.itinerariesToday = this.itineraries.filter(itinerary => {
                let start = new Date(itinerary.start_date);
                let end = new Date(itinerary.end_date);
                return date >= start && date <= end;
            }).length;

            this.cardsSummary.itinerariesPending = this.itineraries.filter(itinerary => itinerary.state == 'PENDING').length

            this.cardsSummary.totalIncome = 0;
            this.itineraries.forEach(itinerary => {
                this.cardsSummary.totalIncome += parseFloat(itinerary.total);
            })
            this.cardsSummary.totalIncome = this.cardsSummary.totalIncome.toFixed(2)
        },
        getCommissions(){
            dashboardRequests.getConciergeCommissionsMetrics(this.currentYear).then(
                res => {
                    if(res.status == 200){
                        let {paidCommissions, pendingCommissions} = res.data

                        this.commissionsAvailable = Array.isArray(paidCommissions) && Array.isArray(pendingCommissions)
                        this.paidCommissions = paidCommissions || [];
                        this.pendingCommissions = pendingCommissions || [];

                        this.prepareChartData()
                    }
                }
            ).catch(error => {
                console.log(error)
            })
        },
        prepareChartData(){
            let paidMonthsValues = [];
            let pendingMonthsValues = [];

            this.paidCommissions.forEach(item => {
                paidMonthsValues.push(item.monthly_commission)
            })

            this.pendingCommissions.forEach(item => {
                pendingMonthsValues.push(item.monthly_commission)
            })

            this.chartData = [
                {
                    label: this.$t("dashboard.auto.chart_paid"),
                    backgroundColor: '#F3764A',
                    data: paidMonthsValues
                },
                {
                    label: this.$t("dashboard.auto.chart_pending"),
                    backgroundColor: '#1ceff4',
                    data: pendingMonthsValues
                },
            ]
        },
        initData(){
            this.getMetrics()
            this.getCommissions()
        }, 
        normalizeDashboardItinerary(itinerary) {
            const startDateTime = itinerary.start_datetime || itinerary.first_start_datetime || itinerary.start_date || null;
            const endDateTime = itinerary.end_datetime || itinerary.first_end_datetime || itinerary.end_date || startDateTime;

            return {
                ...itinerary,
                start_datetime: startDateTime,
                end_datetime: endDateTime,
                start_date: startDateTime,
                end_date: endDateTime,
                total: Number(itinerary.total ?? 0),
                commission_amount: Number(itinerary.commission_amount ?? 0),
            };
        },
        formatDate(dateString) {
            if (!dateString) return this.$t("dashboard.auto.no_date");
            let date = new Date(dateString);
            if (Number.isNaN(date.getTime())) return this.$t("dashboard.auto.no_date");

            return new Intl.DateTimeFormat(this.currentLocale, {
                dateStyle: "medium",
                timeStyle: "short",
                timeZone: "UTC",
            }).format(date);
        },
        loadCommissionsData(value){
            this.currentYear = value
            this.getCommissions()
        },
        // Calendar methods
        getReservationsForDate(date) {
            return this.allItineraries.filter(itinerary => {
                const startDate = new Date(itinerary.start_datetime);
                if (Number.isNaN(startDate.getTime())) return false;
                return startDate.getDate() === date.getDate() &&
                       startDate.getMonth() === date.getMonth() &&
                       startDate.getFullYear() === date.getFullYear();
            });
        },
        getReservationType(date) {
            const now = new Date();
            const reservations = this.getReservationsForDate(date);
            
            if (reservations.length === 0) return null;
            
            // Check if date is in the past (completed) or future (upcoming)
            const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const compareNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            return compareDate < compareNow ? 'completed' : 'upcoming';
        },
        getReservationTypeClass(day) {
            if (!day.isCurrentMonth || !day.hasReservations) return '';
            
            if (day.type === 'completed') {
                return 'bg-green-50 border-green-200 hover:bg-green-100';
            } else if (day.type === 'upcoming') {
                return 'bg-orange-50 border-orange-200 hover:bg-orange-100';
            }
            return '';
        },
        selectDay(day) {
            if (!day.isCurrentMonth || !day.hasReservations) return;
            
            this.selectedDay = day;
            const reservations = this.getReservationsForDate(day.date);
            
            // Format reservations for display
            this.selectedDayReservations = reservations.map(itinerary => {
                const startDate = new Date(itinerary.start_datetime);
                if (Number.isNaN(startDate.getTime())) {
                    return {
                        ...itinerary,
                        dayNumber: day.dayNumber,
                        time: this.$t("dashboard.auto.no_time"),
                        type: day.type
                    };
                }
                const time = new Intl.DateTimeFormat(this.currentLocale, {
                    timeStyle: "short",
                }).format(startDate);
                
                return {
                    ...itinerary,
                    dayNumber: day.dayNumber,
                    time: time,
                    type: day.type
                };
            });
        },
        clearSelection() {
            this.selectedDay = null;
            this.selectedDayReservations = [];
        },
        previousMonth() {
            if (this.currentCalendarMonth === 0) {
                this.currentCalendarMonth = 11;
                this.currentCalendarYear--;
            } else {
                this.currentCalendarMonth--;
            }
            this.clearSelection();
        },
        nextMonth() {
            if (this.currentCalendarMonth === 11) {
                this.currentCalendarMonth = 0;
                this.currentCalendarYear++;
            } else {
                this.currentCalendarMonth++;
            }
            this.clearSelection();
        },
        getTotalReservationsForMonth() {
            const year = this.currentCalendarYear;
            const month = this.currentCalendarMonth;
            
            return this.allItineraries.filter(itinerary => {
                const startDate = new Date(itinerary.start_datetime);
                if (Number.isNaN(startDate.getTime())) return false;
                return startDate.getMonth() === month && startDate.getFullYear() === year;
            }).length;
        }   
    },
    mounted(){
        this.initData()
    }
}
</script>

<style lang="scss" scoped>

</style>
